/**
 * One chat in Recents: a `SidebarRow` with the chat's space mark (or a chat
 * glyph), its title, and a trailing column that shows the status or the time
 * at rest and the row's actions on hover or focus. Double-click or F2
 * renames; right-click, Shift+F10 or the ContextMenu key opens its menu; the
 * hover card has the rest. Memoized with stable callbacks, so a
 * list update re-renders only the rows it changes. Each row follows the
 * minute clock itself, for its relative time and snooze eligibility; only the
 * jump hint follows the held modifiers.
 *
 * @module components/home/RecentThreadRow
 */
import { useAtomValue } from "@effect/atom-react";
import { scopeThreadRef, scopedThreadKey } from "@amoreg/client-runtime/environment";
import type { EnvironmentThreadShell } from "@amoreg/client-runtime/state/models";
import { canSnooze, threadWokeAt } from "@amoreg/client-runtime/state/thread-settled";
import type { ContextMenuPosition, ScopedThreadRef } from "@amoreg/contracts";
import { AlarmClockIcon, AlarmClockOffIcon, CheckIcon, MessageSquareIcon, PinIcon, Undo2Icon } from "lucide-react";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  memo,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "~/lib/utils";
import { contextMenuKeyPosition } from "../../contextMenuKey";
import { useNowMinute } from "../../hooks/useNowMinute";
import {
  shortcutLabelForCommand,
  shouldShowThreadJumpHintsForModifiers,
  threadJumpCommandForIndex,
} from "../../keybindings";
import type { deriveProviderInstanceEntries } from "../../providerInstances";
import { useShortcutModifierState } from "../../shortcutModifierState";
import { primaryServerKeybindingsAtom } from "../../state/server";
import type { Space } from "../../state/spaces";
import { useThreadSelectionStore } from "../../threadSelectionStore";
import { useUiStateStore } from "../../uiStateStore";
import {
  hasUnseenCompletion,
  resolveSettledTimestamp,
  resolveSidebarThreadStatus,
  resolveWorkingStartedAt,
} from "../Sidebar.logic";
import { snoozeWakeLabel, type resolveSnoozePresets } from "../Sidebar.snooze";
import { SpaceGlyph } from "../spaces/SpaceGlyph";
import { SidebarRow } from "../ui/sidebar-row";
import { Tip } from "../ui/tip";
import { Tooltip, TooltipTrigger } from "../ui/tooltip";
import { compactTime, recentStatusKind, type RecentsSection } from "./HomeSidebarRecents.logic";
import { RECENT_STATUS_META } from "./recentThreadStatus";
import { RecentThreadHoverCard } from "./RecentThreadHoverCard";
import { RecentThreadSnoozeButton } from "./RecentThreadSnoozeButton";
import { RecentThreadTitleMatch } from "./RecentThreadTitleMatch";
import { RecentThreadWorkingDuration } from "./RecentThreadWorkingDuration";
import type { RecentThreadHandlers } from "./useRecentThreadActions";

const ACTION_BUTTON_CLASS =
  "inline-flex cursor-pointer items-center gap-1 rounded-md bg-transparent px-1.5 text-xs text-muted-foreground hover:text-foreground";

/** Mod+1..9's key on the first nine rows, shown only while its modifiers are held. */
function RecentThreadJumpHint({ index }: { readonly index: number }) {
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const modifiers = useShortcutModifierState();
  if (!shouldShowThreadJumpHintsForModifiers(modifiers, keybindings, { platform: navigator.platform })) return null;
  const command = threadJumpCommandForIndex(index);
  const label = command ? shortcutLabelForCommand(keybindings, command) : null;
  if (!label) return null;
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute right-1.5 top-1/2 z-10 inline-flex h-5 -translate-y-1/2 items-center rounded-full border border-border/80 bg-background/95 px-1.5 font-mono text-[10px] font-normal text-foreground shadow-sm"
    >
      {label}
    </span>
  );
}

export const RecentThreadRow = memo(function RecentThreadRow(props: {
  readonly thread: EnvironmentThreadShell;
  readonly section: RecentsSection;
  /** The row's place in Recents; the first nine take mod+1..9. */
  readonly index: number;
  readonly space: Space | null;
  readonly providerEntry: ReturnType<typeof deriveProviderInstanceEntries>[number] | null;
  readonly settlementSupported: boolean;
  readonly snoozeSupported: boolean;
  readonly pinningSupported: boolean;
  readonly timestampFormat: Parameters<typeof resolveSnoozePresets>[1];
  readonly isRouteThread: boolean;
  readonly isRenaming: boolean;
  readonly renamingTitle: string;
  readonly onRenameTitleChange: (title: string) => void;
  readonly tabIndex: number;
  readonly onFocusRow: (threadKey: string) => void;
  readonly handlers: RecentThreadHandlers;
  readonly highlight: string;
  readonly onRowClick: (event: ReactMouseEvent, ref: ScopedThreadRef, projectId: string) => void;
  readonly onContextMenu: (threadKey: string, position: ContextMenuPosition) => void;
}) {
  const { thread, section, handlers } = props;
  const threadRef = scopeThreadRef(thread.environmentId, thread.id);
  const threadKey = scopedThreadKey(threadRef);
  const nowMinute = useNowMinute();
  const lastVisitedAt = useUiStateStore((state) => state.threadLastVisitedAtById[threadKey]);
  const isSelected = useThreadSelectionStore((state) => state.selectedThreadKeys.has(threadKey));
  const status = resolveSidebarThreadStatus(thread);
  const isUnread = hasUnseenCompletion({ ...thread, lastVisitedAt });
  const wokeAt = section === "snoozed" ? null : threadWokeAt(thread, { now: new Date().toISOString() });
  const isWoke = wokeAt !== null && (lastVisitedAt === undefined || Date.parse(lastVisitedAt) < Date.parse(wokeAt));
  const statusKind = recentStatusKind({ status, isWoke, isUnread });
  const meta = statusKind ? RECENT_STATUS_META[statusKind] : null;
  const [snoozeOpenRaw, setSnoozeOpen] = useState(false);
  const showSnooze =
    section !== "settled" &&
    section !== "snoozed" &&
    props.snoozeSupported &&
    canSnooze(thread, { now: `${nowMinute}:00.000Z` });
  const snoozeOpen = snoozeOpenRaw && showSnooze;
  const renameDone = useRef(false);
  useEffect(() => {
    if (props.isRenaming) renameDone.current = false;
  }, [props.isRenaming]);

  const stop = (event: ReactMouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const title = props.isRenaming ? (
    <input
      autoFocus
      value={props.renamingTitle}
      aria-label="Thread title"
      onChange={(event) => props.onRenameTitleChange(event.target.value)}
      onFocus={(event) => event.currentTarget.select()}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Enter") {
          event.preventDefault();
          renameDone.current = true;
          void handlers.commitRename(threadRef, props.renamingTitle, thread.title);
        } else if (event.key === "Escape") {
          event.preventDefault();
          renameDone.current = true;
          handlers.cancelRename();
        }
      }}
      onBlur={() => {
        if (!renameDone.current) void handlers.commitRename(threadRef, props.renamingTitle, thread.title);
      }}
      className="min-w-0 flex-1 rounded-sm border border-input bg-card px-1 text-sm text-card-foreground outline-none focus:border-foreground"
    />
  ) : (
    <span
      data-recent-title=""
      className={cn(
        "min-w-0 flex-1 truncate text-start text-[13px] leading-[calc(1.25/0.875)] font-normal transition-colors motion-reduce:transition-none",
        thread.titleRegeneration != null && "opacity-55",
      )}
    >
      <RecentThreadTitleMatch text={thread.title} query={props.highlight} />
    </span>
  );

  const pin =
    thread.pinnedAt != null ? (
      <Tip label="Unpin">
        <button
          type="button"
          aria-label="Unpin thread"
          disabled={!props.pinningSupported}
          onClick={(event) => {
            stop(event);
            void handlers.unpin(threadRef);
          }}
          className="inline-flex shrink-0 cursor-pointer items-center rounded-sm text-muted-foreground/65 hover:text-foreground"
        >
          <PinIcon aria-hidden strokeWidth={1.75} className="size-3" />
        </button>
      </Tip>
    ) : null;

  const statusLabel = meta ? (
    statusKind === "woke" && wokeAt ? (
      <button
        type="button"
        aria-label="Dismiss Woke notification"
        onClick={(event) => {
          stop(event);
          handlers.acknowledgeWoke(threadRef, wokeAt);
        }}
        className={cn("pointer-events-auto inline-flex cursor-pointer items-center gap-1 rounded-sm text-xs font-normal hover:underline", meta.className)}
      >
        <meta.Icon strokeWidth={1.75} className="size-3.5" />
        Woke
      </button>
    ) : (
      // The word stays for screen readers; the glyph and elapsed time carry it on screen.
      <span className={cn("inline-flex items-center gap-1 text-xs font-normal", meta.className)}>
        <meta.Icon strokeWidth={1.75} className="size-3.5 shrink-0" />
        <span className="sr-only">{meta.label}</span>
        {statusKind === "working" ? <RecentThreadWorkingDuration startedAt={resolveWorkingStartedAt(thread)} /> : null}
      </span>
    )
  ) : null;

  const restLabel =
    section === "snoozed" && thread.snoozedUntil ? (
      <span className="inline-flex items-center gap-1 text-xs text-info-foreground tabular-nums">
        <AlarmClockIcon aria-hidden strokeWidth={1.75} className="size-3" />
        {snoozeWakeLabel(thread.snoozedUntil, { now: new Date().toISOString() })}
      </span>
    ) : section === "settled" ? (
      <span className="text-xs tabular-nums">{compactTime(resolveSettledTimestamp(thread))}</span>
    ) : (
      (statusLabel ?? (
        <span className="text-xs tabular-nums">{compactTime(thread.latestUserMessageAt ?? thread.updatedAt)}</span>
      ))
    );

  const actions =
    section === "snoozed" ? (
      props.snoozeSupported ? (
        <Tip label="Wake now">
          <button
            type="button"
            aria-label="Wake thread now"
            onClick={(event) => {
              stop(event);
              void handlers.unsnooze(threadRef);
            }}
            className={ACTION_BUTTON_CLASS}
          >
            <AlarmClockOffIcon strokeWidth={1.75} className="size-3.5" />
          </button>
        </Tip>
      ) : null
    ) : section === "settled" ? (
      props.settlementSupported ? (
        <Tip label="Un-settle">
          <button
            type="button"
            aria-label="Un-settle thread"
            data-recent-unsettle=""
            onClick={(event) => {
              stop(event);
              void handlers.unsettle(threadRef);
            }}
            className={cn(ACTION_BUTTON_CLASS, "-mr-1")}
          >
            <Undo2Icon strokeWidth={1.75} className="size-3.5" />
          </button>
        </Tip>
      ) : null
    ) : (
      <>
        {showSnooze ? (
          <RecentThreadSnoozeButton
            open={snoozeOpen}
            onOpenChange={setSnoozeOpen}
            onSnooze={(preset) => void handlers.snooze(threadRef, preset)}
            timestampFormat={props.timestampFormat}
          />
        ) : null}
        {props.settlementSupported ? (
          <Tip label="Settle">
            <button
              type="button"
              aria-label="Settle thread"
              data-recent-settle=""
              onClick={(event) => {
                stop(event);
                void handlers.settle(threadRef);
              }}
              className={cn(ACTION_BUTTON_CLASS, "-mr-1")}
            >
              <CheckIcon strokeWidth={1.75} className="size-3.5" />
            </button>
          </Tip>
        ) : null}
      </>
    );

  // Status (or time) at rest; on hover or focus the actions take its place.
  const trailing = (
    <span className="group/slot relative ml-auto flex h-6 min-w-8 shrink-0 items-stretch justify-end text-xs tabular-nums">
      <span
        className={cn(
          "pointer-events-none flex items-center self-center tabular-nums text-secondary-label transition-opacity duration-150",
          statusKind === "woke"
            ? "pointer-events-auto"
            : "group-hover/sidebar-row:absolute group-hover/sidebar-row:right-0 group-hover/sidebar-row:opacity-0 group-has-[:focus-visible]/slot:absolute group-has-[:focus-visible]/slot:right-0 group-has-[:focus-visible]/slot:opacity-0",
          snoozeOpen && "absolute right-0 opacity-0",
        )}
      >
        {restLabel}
      </span>
      <span
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 flex items-stretch opacity-0 transition-opacity duration-150 has-[:focus-visible]:pointer-events-auto has-[:focus-visible]:static has-[:focus-visible]:opacity-100 group-hover/sidebar-row:pointer-events-auto group-hover/sidebar-row:static group-hover/sidebar-row:opacity-100",
          snoozeOpen && "pointer-events-auto static opacity-100",
        )}
      >
        {actions}
      </span>
    </span>
  );

  return (
    <li role="none" className="relative list-none [content-visibility:auto] [contain-intrinsic-size:auto_32px]">
      <Tooltip>
        <TooltipTrigger
          delay={150}
          render={
            <SidebarRow
              render={<div />}
              data-recent-thread=""
              data-section={section}
              data-status={statusKind ?? "none"}
              data-selected={isSelected ? "" : undefined}
              aria-selected={isSelected}
              aria-current={props.isRouteThread ? "page" : undefined}
              role="treeitem"
              aria-level={section === "snoozed" || section === "settled" ? 2 : 1}
              tabIndex={props.tabIndex}
              aria-label={`${thread.title}${meta ? `, ${meta.label}` : ""}`}
              onFocus={() => props.onFocusRow(threadKey)}
              onClick={(event: ReactMouseEvent) => {
                if (props.isRenaming) return;
                props.onRowClick(event, threadRef, thread.projectId);
              }}
              onDoubleClick={(event: ReactMouseEvent) => {
                if ((event.target as HTMLElement).closest("button, a, input")) return;
                event.preventDefault();
                handlers.startRename(threadRef, thread.title);
              }}
              onKeyDown={(event: ReactKeyboardEvent) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handlers.open(threadRef, thread.projectId);
                  return;
                }
                if (event.key === "F2") {
                  event.preventDefault();
                  handlers.startRename(threadRef, thread.title);
                  return;
                }
                const menuAt = contextMenuKeyPosition(event, event.currentTarget);
                if (menuAt) {
                  event.preventDefault();
                  props.onContextMenu(threadKey, menuAt);
                }
              }}
              onContextMenu={(event: ReactMouseEvent) => {
                event.preventDefault();
                props.onContextMenu(threadKey, { x: event.clientX, y: event.clientY });
              }}
              className={cn(
                "group/sidebar-row relative select-none focus-visible:ring-inset",
                // At rest a settled chat recedes to the secondary label, all but its title.
                section === "settled" &&
                  "[&:not(:hover):not(:focus-within)_:not([data-recent-title]):not([data-recent-icon])]:text-secondary-label",
                props.isRouteThread
                  ? "bg-sidebar-row-active text-sidebar-foreground hover:bg-sidebar-row-active"
                  : isSelected && "bg-sidebar-row-selected hover:bg-sidebar-row-selected",
              )}
            />
          }
        >
          <span data-recent-icon="" className="flex size-4 shrink-0 items-center justify-center">
            {props.space ? (
              <SpaceGlyph space={props.space} className="size-4 rounded-[4px] text-[9px] leading-none" />
            ) : (
              <MessageSquareIcon strokeWidth={1.75} className="size-4" />
            )}
          </span>
          {title}
          {pin}
          {trailing}
          {props.index < 9 ? <RecentThreadJumpHint index={props.index} /> : null}
        </TooltipTrigger>
        {snoozeOpen ? null : (
          <RecentThreadHoverCard
            thread={thread}
            section={section}
            space={props.space}
            providerEntry={props.providerEntry}
            status={meta}
          />
        )}
      </Tooltip>
    </li>
  );
});
